import { plural, t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { type FacilityTransferActivityPublicRecord, StorageTransferOrderActivityEventType } from '@navio/nsdk';
import { NoddiButton } from 'noddi-ui-web';
import { formatInTimeZone, NoddiDateFormats, noddiFormatDate } from 'noddi-util';
import { useState } from 'react';
import { useFacilityTimeZone } from './facilityTimeZone';
import { formatOrderSlugs } from './formatOrderSlugs';

interface Props {
  changes: FacilityTransferActivityPublicRecord[];
}

export const RecentChanges = ({ changes }: Props) => {
  const timeZone = useFacilityTimeZone();
  const [isExpanded, setIsExpanded] = useState(false);
  const latest = changes[0];
  if (latest == null) {
    return null;
  }
  const latestLabel = formatInTimeZone(latest.createdAt, NoddiDateFormats.DATE_TIME, timeZone);
  const mergedChanges = mergeSameChanges(changes, timeZone);
  const changeCount = mergedChanges.length;

  return (
    <div className='flex flex-col gap-2 text-sm'>
      <div className='flex flex-wrap items-center gap-x-2'>
        <span className='text-muted-foreground'>
          <Trans>Last changed {latestLabel}</Trans>
        </span>
        <NoddiButton
          variant='link'
          size='extraSmall'
          className='px-0'
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded((prev) => !prev)}
        >
          {isExpanded ? <Trans>Hide changes</Trans> : <Trans>Show changes ({changeCount})</Trans>}
        </NoddiButton>
      </div>
      {isExpanded && (
        <ul className='flex flex-col gap-1'>
          {mergedChanges.map((change) => (
            <li key={change.key}>
              <span className='mr-1 text-xs text-muted-foreground uppercase'>
                {formatOrderSlugs(change.orderSlugs)}
              </span>
              <span className='font-medium'>{change.description}</span>
              <span className='text-muted-foreground'> — {change.timeLabel}</span>
              {change.wheelSets.length > 0 && <p className='text-muted-foreground'>{change.wheelSets.join(', ')}</p>}
              {change.comment && <p className='border-l-2 border-border pl-2 whitespace-pre-wrap'>{change.comment}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

type MergedChange = {
  key: string;
  description: string;
  timeLabel: string;
  orderSlugs: string[];
  wheelSets: string[];
  comment: string;
};

// Dispatch writes the same change on every order of a day at once; the facility needs to read it once.
// Wheel-set changes name their own wheel sets, so they stay one entry per order.
const mergeSameChanges = (changes: FacilityTransferActivityPublicRecord[], timeZone: string) => {
  const merged: MergedChange[] = [];
  for (const change of changes) {
    const description = describeChange(change, timeZone);
    const timeLabel = formatInTimeZone(change.createdAt, NoddiDateFormats.DATE_TIME, timeZone);
    const wheelSets = change.storageObjectGroups.map((group) => group.metadata.name);
    const isPlain = wheelSets.length === 0 && change.comment === '';
    const same = isPlain
      ? merged.find(
          (entry) =>
            entry.description === description &&
            entry.timeLabel === timeLabel &&
            entry.wheelSets.length === 0 &&
            entry.comment === ''
        )
      : undefined;
    if (same) {
      same.orderSlugs.push(change.orderSlug);
    } else {
      merged.push({
        key: String(change.id),
        description,
        timeLabel,
        orderSlugs: [change.orderSlug],
        wheelSets,
        comment: change.comment
      });
    }
  }
  return merged;
};

// `detail` is stored backend-side in English, so the text is built from the event type instead.
const describeChange = (change: FacilityTransferActivityPublicRecord, timeZone: string): string => {
  switch (change.eventType) {
    case StorageTransferOrderActivityEventType.EXPECTED_PICKUP_CHANGED: {
      if (change.expectedPickupTo == null) {
        return t`Expected pickup time changed`;
      }
      const when = formatInTimeZone(change.expectedPickupTo, NoddiDateFormats.DATE_TIME, timeZone);
      return t`Expected pickup changed to ${when}`;
    }
    case StorageTransferOrderActivityEventType.EXPECTED_DELIVERY_CHANGED: {
      if (change.expectedDeliveryTo == null) {
        return t`Expected delivery time changed`;
      }
      const when = formatInTimeZone(change.expectedDeliveryTo, NoddiDateFormats.DATE_TIME, timeZone);
      return t`Expected delivery changed to ${when}`;
    }
    case StorageTransferOrderActivityEventType.SCHEDULED_DATE_CHANGED: {
      if (change.scheduledDateTo == null) {
        return t`Scheduled date changed`;
      }
      const date = noddiFormatDate(change.scheduledDateTo, NoddiDateFormats.DATE);
      return t`Scheduled date changed to ${date}`;
    }
    case StorageTransferOrderActivityEventType.ITEMS_ADDED:
      return change.storageObjectGroups.length === 0
        ? t`Wheel sets added`
        : plural(change.storageObjectGroups.length, { one: '# wheel set added', other: '# wheel sets added' });
    case StorageTransferOrderActivityEventType.ITEMS_REMOVED:
      return change.storageObjectGroups.length === 0
        ? t`Wheel sets removed`
        : plural(change.storageObjectGroups.length, { one: '# wheel set removed', other: '# wheel sets removed' });
    case StorageTransferOrderActivityEventType.RECIPIENTS_CHANGED:
      return t`Email recipients changed`;
    case StorageTransferOrderActivityEventType.COMMENT_CHANGED:
      return t`Comment changed`;
    case StorageTransferOrderActivityEventType.PORTS_CHANGED:
      return t`Port changed`;
    case StorageTransferOrderActivityEventType.NOTE_ADDED:
      return t`Note added`;
    case StorageTransferOrderActivityEventType.CANCELLED:
      return t`Transfer order cancelled`;
  }
};
